import { Button, TextInput } from "@mantine/core";
import type React from "react";
import { useState } from "react";
import type { LocalPet } from "../../../types/portal";
import EmptyState from "../../shared/EmptyState";

interface PetCardProps {
  pet: LocalPet;
  onUpdate: (petId: string, data: Record<string, unknown>) => void;
  onDelete: (petId: string) => void;
}

// Defined outside PetsTab so internal edit state isn't lost when the parent re-renders.
const PetCard: React.FC<PetCardProps> = ({ pet, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    pet_name: pet.pet_name,
    age: pet.age,
    race: pet.race,
    target_behaviors: pet.target_behaviors || "",
  });

  const handleSave = (): void => {
    onUpdate(pet.id, editData);
    setIsEditing(false);
  };

  const handleCancel = (): void => {
    setEditData({
      pet_name: pet.pet_name,
      age: pet.age,
      race: pet.race,
      target_behaviors: pet.target_behaviors || "",
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-muted rounded-xl p-5 border border-border shadow-sm transition-all duration-200 relative min-h-[280px] flex flex-col">
      <div className="flex justify-end items-start mb-3">
        <div className="flex gap-1">
          {!isEditing ? (
            <>
              <Button
                size="compact-sm"
                color="blue"
                onClick={() => setIsEditing(true)}
                title="Edit"
              >
                Edit
              </Button>
              <Button
                size="compact-sm"
                color="red"
                onClick={() => onDelete(pet.id)}
                title="Delete"
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              <Button size="compact-sm" color="green" onClick={handleSave} title="Save">
                Save
              </Button>
              <Button size="compact-sm" color="gray" onClick={handleCancel} title="Cancel">
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="text-center mb-3">
        {pet.photo_url ? (
          <img
            src={pet.photo_url}
            alt={pet.pet_name || "Pet"}
            className="w-[120px] h-[120px] rounded-full object-cover border-[3px] border-primary mx-auto"
          />
        ) : (
          <div className="text-5xl text-center mb-3">🐾</div>
        )}
      </div>

      {isEditing ? (
        <div className="flex flex-col gap-2.5">
          <div>
            <label className="block w-full text-xs text-muted-foreground mb-1 font-medium">
              Pet Name
            </label>
            <TextInput
              value={editData.pet_name}
              onChange={(e) => setEditData({ ...editData, pet_name: e.target.value })}
              placeholder="Name"
              size="sm"
              classNames={{ input: "bg-input border-border text-foreground" }}
            />
          </div>
          <div>
            <label className="block w-full text-xs text-muted-foreground mb-1 font-medium">
              Age (years)
            </label>
            <TextInput
              type="number"
              value={String(editData.age)}
              onChange={(e) => setEditData({ ...editData, age: e.target.value })}
              placeholder="Age"
              min={0}
              max={30}
              size="sm"
              classNames={{ input: "bg-input border-border text-foreground" }}
            />
          </div>
          <div>
            <label className="block w-full text-xs text-muted-foreground mb-1 font-medium">
              Breed
            </label>
            <TextInput
              value={editData.race}
              onChange={(e) => setEditData({ ...editData, race: e.target.value })}
              placeholder="Breed"
              size="sm"
              classNames={{ input: "bg-input border-border text-foreground" }}
            />
          </div>
          <div>
            <label className="block w-full text-xs text-muted-foreground mb-1 font-medium">
              Target Behaviors
            </label>
            <TextInput
              value={editData.target_behaviors}
              onChange={(e) => setEditData({ ...editData, target_behaviors: e.target.value })}
              placeholder="Target Behaviors"
              size="sm"
              classNames={{ input: "bg-input border-border text-foreground" }}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col gap-2">
          <div className="text-xl font-bold text-foreground mb-2 text-center">{pet.pet_name}</div>
          <div className="flex flex-col gap-1">
            <div className="text-sm text-muted-foreground text-center">{pet.age} years old</div>
            <div className="text-sm text-muted-foreground text-center">{pet.race || "Mixed"}</div>
          </div>
          {pet.target_behaviors && (
            <div className="text-xs text-muted-foreground text-center mt-2 italic">
              {pet.target_behaviors}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface PetsTabProps {
  pets: LocalPet[];
  loading: boolean;
  error: string | null;
  onAddPet: () => void;
  onUpdatePet: (petId: string, data: Record<string, unknown>) => void;
  onDeletePet: (petId: string) => void;
}

const PetsTab: React.FC<PetsTabProps> = ({
  pets,
  loading,
  error,
  onAddPet,
  onUpdatePet,
  onDeletePet,
}) => (
  <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
      <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">My Pets</h2>
      <Button onClick={onAddPet} radius="md">
        + Add Pet
      </Button>
    </div>
    {loading ? (
      <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
        Loading pets...
      </div>
    ) : error ? (
      <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
        {error}
      </div>
    ) : pets.length === 0 ? (
      <EmptyState icon="No Pets" description="No pets registered yet" />
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 max-h-[600px] overflow-y-auto pr-2">
        {pets.map((pet, index) => (
          <PetCard
            key={pet.id || index}
            pet={pet}
            onUpdate={onUpdatePet}
            onDelete={onDeletePet}
          />
        ))}
      </div>
    )}
  </div>
);

export default PetsTab;
